import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconCheck,
    IconCopy,
    IconDownload,
    IconFileTypePdf,
    IconFileZip,
    IconStack2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import OutputFormatSelect from '../shared/OutputFormatSelect';
import { QR_OUTPUT_FORMATS } from '../shared/imageOutputFormats';
import QrDesignPanel from './QrDesignPanel';
import QrPrintShare from './QrPrintShare';
import QrHistoryCard from './QrHistoryCard';
import QrPreviewCard, { QrTipsCard } from './QrPreviewCard';
import { QrScoreCard } from './QrScoreCard';
import { PresetThumb } from './QrStyleThumbs';
import { STYLE_PRESETS, applyStylePreset, isPresetActive } from './qrStylePresets';
import { LogoPresetTile, findLogoPreset, logoIdForContentType, tileNodeToDataUrl } from './qrLogoPresets';
import QrSelect from './QrSelect';
import QrTypeIcon from './QrTypeIcon';
import { Field, SegRow, StepCard, SuggestInput, inputClass, swatchBtnClass } from './QrUiBits';
import { auditQrDesign, decodeQrArtwork } from './qrAudit';
import {
    MAX_BULK_ROWS,
    buildBulkPdf,
    buildBulkZip,
    parseBulkInput,
    renderBulkPreviews,
} from './qrBulk';
import {
    clearHistory,
    deleteHistoryEntry,
    deleteTemplate,
    loadHistory,
    loadTemplates,
    saveHistoryEntry,
    saveTemplate,
} from './qrStorage';
import {
    CONTENT_TYPES,
    CRYPTO_COINS,
    DEFAULT_DESIGN,
    PAYMENT_PROVIDERS,
    buildQrPayload,
    exportQrCode,
    isQrPayloadValid,
    isSupportedLogoFile,
    outputFileName,
    readFileAsDataUrl,
    renderPreview,
    renderQrCanvas,
    validateQrContent,
} from './qrCodeEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';
import Hint from '../../../../../components/common/Hint';

const BULK_SAMPLE = 'https://sohozkaj.com, homepage\nhttps://sohozkaj.com/tools/image/qr-code, qr-tool\nhttps://sohozkaj.com/tools/image/compress, compress\n+8801712345678, office-phone';

const BULK_ACTION = 'h-10 rounded-xl border border-[#ECE8E4] bg-white px-3.5 text-[13px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300';

/** Thumbnails drawn while bulk mode is on — the batch shares one design. */
const BULK_PREVIEW_COUNT = 4;

/** Types shown as chips; the rest stay behind the "other types" dropdown. */
const QUICK_CONTENT_TYPES = ['url', 'phone', 'wifi', 'facebook', 'youtube', 'whatsapp'];

/**
 * Shape of a logo the content type dropped in by itself: small enough not to eat
 * the error-correction budget, softly rounded, and sitting in a cleared patch so
 * the mark reads against the modules. A logo the user picks keeps the panel's
 * plain defaults instead — this is only the automatic one.
 */
const AUTO_LOGO_DESIGN = {
    logoSizePercent: 20,
    logoRadiusPercent: 20,
    hideBackgroundDots: true,
    logoMarginPercent: 0.5,
};
const OTHER_CONTENT_TYPES = CONTENT_TYPES.filter((type) => !QUICK_CONTENT_TYPES.includes(type));

const DEFAULT_FIELDS = {
    url: '',
    text: '',
    ssid: '',
    password: '',
    encryption: 'wpa',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    organization: '',
    jobTitle: '',
    website: '',
    address: '',
    emailTo: '',
    emailSubject: '',
    emailBody: '',
    phoneNumber: '',
    smsNumber: '',
    smsMessage: '',
    latitude: '',
    longitude: '',
    locationLabel: '',
    eventTitle: '',
    eventLocation: '',
    eventStart: '',
    eventEnd: '',
    eventDescription: '',
    whatsappNumber: '',
    whatsappMessage: '',
    facebookUser: '',
    instagramUser: '',
    twitterUser: '',
    linkedinUser: '',
    telegramUser: '',
    youtubeUrl: '',
    cryptoCoin: 'bitcoin',
    cryptoAddress: '',
    cryptoAmount: '',
    cryptoLabel: '',
    cryptoMessage: '',
    locationAddress: '',
    paymentProvider: 'bkash',
    paymentNumber: '',
    paymentAmount: '',
    paymentNote: '',
    appIosUrl: '',
    appAndroidUrl: '',
};

const WIFI_ENCRYPTIONS = ['wpa', 'wep', 'nopass'];

/** Preview is rendered at a fixed working size, independent of the export size. */
const PREVIEW_SIZE = 512;

/**
 * Grid for any number of copies: as square as the count allows, and portrait
 * counts (2, 6) get their extra room down the page rather than across it.
 */
function sheetLayout(perPage) {
    const columns = Math.max(1, Math.ceil(Math.sqrt(perPage)));
    return { columns, rows: Math.ceil(perPage / columns) };
}

const PAGE_MARGIN_MM = 10;
/** A4 minus the page margin on each side. */
const PRINTABLE_MM = { w: 210 - PAGE_MARGIN_MM * 2, h: 297 - PAGE_MARGIN_MM * 2 };

/** A saved template's style, shaped like a style preset so PresetThumb can draw it. */
function templateThumbStyle(style) {
    return { id: `tpl-${style.fgColor}-${style.dotStyle}`, ...style };
}

export default function QrCodeImage() {
    const { t } = useTranslation();

    const [contentType, setContentType] = useState('url');
    const [fields, setFields] = useState(DEFAULT_FIELDS);
    const [design, setDesign] = useState(DEFAULT_DESIGN);
    const [logoName, setLogoName] = useState('');
    const [size, setSize] = useState(600);

    const [mode, setMode] = useState('single');

    const [preview, setPreview] = useState(null);
    const [rendering, setRendering] = useState(false);
    const [downloadingFormat, setDownloadingFormat] = useState(null);
    // What the download button will write. The bar's picker sets it; copy,
    // share and the batch exports ask for their own format regardless.
    const [format, setFormat] = useState('png');
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    const [templates, setTemplates] = useState([]);
    const [history, setHistory] = useState([]);

    const [verification, setVerification] = useState(null);
    const [verifying, setVerifying] = useState(false);

    const [bulkText, setBulkText] = useState('');
    const [bulkPreviews, setBulkPreviews] = useState([]);
    const [bulkRendering, setBulkRendering] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(null);
    const [bulkProgress, setBulkProgress] = useState(null);

    const renderTokenRef = useRef(0);
    const bulkTokenRef = useRef(0);
    const verifyTokenRef = useRef(0);
    // Remembers an uploaded logo so switching to a preset and back keeps it.
    const customLogoRef = useRef(null);
    // True while the centre logo is whatever the content type suggested. Any
    // deliberate logo choice (preset, upload, or "no logo") clears it, and from
    // then on switching type leaves the logo alone.
    const logoAutoRef = useRef(true);
    const logoTileRef = useRef(null);
    const printingRef = useRef(false);

    const patchDesign = useCallback((partial) => {
        setDesign((prev) => ({ ...prev, ...partial }));
    }, []);

    const updateField = useCallback((key, value) => {
        setFields((prev) => ({ ...prev, [key]: value }));
    }, []);

    useEffect(() => {
        setTemplates(loadTemplates());
        setHistory(loadHistory());
    }, []);

    // The pasted list drives both the batch and, in bulk mode, the code on
    // show: preview, score, per-format downloads, print sheet and share links
    // all act on its first row, so nothing in the rail goes dead.
    const bulkRows = useMemo(() => parseBulkInput(bulkText), [bulkText]);
    const payload = mode === 'bulk'
        ? (bulkRows[0]?.value || '')
        : buildQrPayload(contentType, fields);
    // Field errors gate the preview: a half-typed email should not become a code.
    const fieldErrors = useMemo(
        () => validateQrContent(contentType, fields, design.errorLevel),
        [contentType, fields, design.errorLevel],
    );
    const hasFieldErrors = Object.keys(fieldErrors).length > 0;
    const errorFor = useCallback(
        (key) => (fieldErrors[key] ? t(`qrCode.validation.${fieldErrors[key]}`) : undefined),
        [fieldErrors, t],
    );
    const hasContent = mode === 'bulk'
        ? bulkRows.length > 0
        : isQrPayloadValid(contentType, fields) && !hasFieldErrors;

    // What was typed into this field on codes saved earlier, newest first.
    const suggestionsFor = useCallback((key) => {
        const seen = [];
        history.forEach((entry) => {
            const value = String(entry.fields?.[key] || '').trim();
            if (value && !seen.includes(value)) seen.push(value);
        });
        return seen.slice(0, 8);
    }, [history]);

    /**
     * Forget one suggestion. Suggestions are a view over saved codes, so this
     * drops every saved code that carried this value in that field.
     */
    const forgetSuggestion = useCallback((key, value) => {
        let next = history;
        history
            .filter((entry) => String(entry.fields?.[key] || '').trim() === value)
            .forEach((entry) => { next = deleteHistoryEntry(entry.id); });
        setHistory(next);
    }, [history]);

    const suggestProps = useCallback((key) => ({
        suggestKey: key,
        suggestions: suggestionsFor(key),
        onRemove: (value) => forgetSuggestion(key, value),
        removeLabel: t('qrCode.suggestionRemove'),
        onClearAll: () => setHistory(clearHistory()),
        clearAllLabel: t('qrCode.suggestionClearAll'),
    }), [suggestionsFor, forgetSuggestion, t]);

    // Live preview: every content or design change re-renders after a short debounce.
    useEffect(() => {
        if (!hasContent) {
            renderTokenRef.current += 1;
            setPreview(null);
            setRendering(false);
            // Too much data is worth saying out loud; a half-filled form is not.
            setError(fieldErrors._payload ? t('qrCode.validation.capacity') : null);
            return undefined;
        }

        const token = ++renderTokenRef.current;
        setRendering(true);

        const timer = setTimeout(async () => {
            try {
                const next = await renderPreview(payload, design, PREVIEW_SIZE);
                if (renderTokenRef.current !== token) return;
                setPreview(next);
                setError(null);
            } catch {
                if (renderTokenRef.current !== token) return;
                setPreview(null);
                setError(t('qrCode.errors.failed'));
            } finally {
                if (renderTokenRef.current === token) setRendering(false);
            }
        }, 220);

        return () => clearTimeout(timer);
    }, [payload, design, hasContent, fieldErrors._payload, t]);

    // Bulk previews are rebuilt whenever the list or the shared design changes.
    useEffect(() => {
        if (mode !== 'bulk' || !bulkRows.length) {
            setBulkPreviews([]);
            return undefined;
        }
        const token = ++bulkTokenRef.current;
        setBulkRendering(true);
        const timer = setTimeout(async () => {
            // Only the first few: sixty codes redrawn on every slider nudge
            // would freeze the tab, and they all wear the same design anyway.
            const rendered = await renderBulkPreviews(bulkRows.slice(0, BULK_PREVIEW_COUNT), design, 220);
            if (bulkTokenRef.current !== token) return;
            setBulkPreviews(rendered);
            setBulkRendering(false);
        }, 260);
        return () => clearTimeout(timer);
    }, [mode, bulkRows, design]);

    // Every settled design is decoded back with a real reader, so the score
    // reflects what a scanner sees rather than what the rules predict.
    useEffect(() => {
        if (!hasContent) {
            setVerification(null);
            setVerifying(false);
            return undefined;
        }
        const token = ++verifyTokenRef.current;
        setVerifying(true);
        const timer = setTimeout(async () => {
            const result = await decodeQrArtwork(payload, design);
            if (verifyTokenRef.current !== token) return;
            setVerification(result);
            setVerifying(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [hasContent, payload, design]);

    const audit = useMemo(
        () => auditQrDesign(design, payload, verification),
        [design, payload, verification],
    );

    const rememberCode = useCallback(async () => {
        if (!hasContent) return;
        try {
            const blob = await exportQrCode(payload, { ...design, frameType: 'none' }, { size: 150, format: 'png' });
            const thumbnail = await readFileAsDataUrl(blob);
            setHistory(saveHistoryEntry({ type: contentType, payload, fields, design, thumbnail }));
        } catch {
            // A missing thumbnail is not worth interrupting a download for.
        }
    }, [contentType, design, fields, hasContent, payload]);

    const handleOpenHistory = (entry) => {
        // The saved design carries its own logo — the type must not restyle it.
        logoAutoRef.current = false;
        setContentType(entry.type);
        setFields({ ...DEFAULT_FIELDS, ...(entry.fields || {}) });
        setDesign((prev) => ({ ...prev, ...(entry.style || {}) }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSaveTemplate = () => {
        setTemplates(saveTemplate(design, t('qrCode.templateName', { n: toBnDigits(templates.length + 1) })));
    };

    // The active preset is rendered hidden below; this reads that node back and
    // stores it as the logo, so tile colours and the QR always agree.
    const activePreset = findLogoPreset(design.logoId);
    useEffect(() => {
        if (!activePreset) return;
        const dataUrl = tileNodeToDataUrl(logoTileRef.current?.firstElementChild);
        if (dataUrl) patchDesign({ logoDataUrl: dataUrl });
    }, [activePreset, design.logoBgColor, design.logoIconColor, design.logoPlate, patchDesign]);

    /** Put a built-in preset in the centre, on its own palette (the effect above renders it). */
    const applyPresetLogo = useCallback((id, extra) => {
        const preset = findLogoPreset(id);
        if (!preset) return;
        setLogoName(t(`qrCode.logoNames.${id}`));
        patchDesign({
            logoId: id,
            logoPlate: true,
            logoBgColor: preset.color,
            logoIconColor: '#ffffff',
            ...extra,
        });
    }, [patchDesign, t]);

    const clearLogo = useCallback(() => {
        setLogoName('');
        patchDesign({ logoId: 'none', logoDataUrl: null });
    }, [patchDesign]);

    /**
     * Switching content type also fits the matching logo — WiFi gets the WiFi
     * mark, YouTube the YouTube one — until the user picks a logo of their own,
     * after which the type never touches it again.
     */
    const handleContentType = useCallback((type) => {
        setContentType(type);
        if (!logoAutoRef.current) return;
        const id = logoIdForContentType(type);
        if (id) applyPresetLogo(id, AUTO_LOGO_DESIGN);
        else clearLogo();
    }, [applyPresetLogo, clearLogo]);

    const handleLogoSelect = (id) => {
        logoAutoRef.current = false;
        if (id === 'none') {
            clearLogo();
            return;
        }
        if (id === 'custom') {
            if (!customLogoRef.current) return;
            setLogoName(customLogoRef.current.name);
            patchDesign({ logoId: 'custom', logoDataUrl: customLogoRef.current.dataUrl });
            return;
        }
        applyPresetLogo(id);
    };

    const handleLogoPick = async (incoming) => {
        const file = Array.from(incoming || [])[0];
        if (!file) return;
        if (!isSupportedLogoFile(file)) {
            const msg = t('qrCode.errors.invalidLogo');
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        try {
            const dataUrl = await readFileAsDataUrl(file);
            customLogoRef.current = { name: file.name, dataUrl };
            logoAutoRef.current = false;
            setLogoName(file.name);
            patchDesign({ logoId: 'custom', logoDataUrl: dataUrl });
        } catch {
            notifications.show({ title: t('common.error'), message: t('qrCode.errors.invalidLogo'), color: 'red' });
        }
    };

    const handleLogoRemove = () => {
        customLogoRef.current = null;
        logoAutoRef.current = false;
        clearLogo();
    };

    /** `pick` rather than the picked format: share and the batch want PNG. */
    const handleDownload = async (pick) => {
        if (!hasContent || downloadingFormat) return;

        recordToolUsage('image/qr-code', { category: 'image' });
        setDownloadingFormat(pick);
        try {
            const blob = await exportQrCode(payload, design, { size, format: pick });
            downloadBlob(blob, outputFileName(pick, blob));
            void rememberCode();
            notifications.show({ title: t('common.success'), message: t('qrCode.done'), color: 'green' });
        } catch {
            const msg = t('qrCode.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setDownloadingFormat(null);
        }
    };

    // Clipboard images are PNG only, and `write` needs the promise handed over
    // inside the click so Safari keeps the user gesture alive.
    const handleCopy = async () => {
        if (!hasContent) return;
        if (!navigator.clipboard?.write || typeof window.ClipboardItem === 'undefined') {
            notifications.show({ title: t('common.error'), message: t('qrCode.errors.copyFailed'), color: 'red' });
            return;
        }
        try {
            const blob = await exportQrCode(payload, design, { size, format: 'png' });
            await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            notifications.show({ title: t('common.error'), message: t('qrCode.errors.copyFailed'), color: 'red' });
        }
    };

    // Printed from a hidden iframe rather than a popup: no blocker to trip over,
    // and the print dialog stays attached to this tab. `perPage` lays the same
    // code out as a cut-up sheet of stickers.
    const handlePrint = async (perPage = 1) => {
        if (!hasContent || printingRef.current) return;
        printingRef.current = true;
        try {
            const canvas = await renderQrCanvas(payload, design, size);
            const dataUrl = canvas.toDataURL('image/png');

            // Everything below is in millimetres so the sheet always fits one A4
            // page: percentage widths let 2-up spill onto a second page, and a
            // framed code (taller than wide) made it worse.
            const { columns, rows } = sheetLayout(perPage);
            const GAP = 6;
            const CELL_PAD = 3;
            const cellW = (PRINTABLE_MM.w - GAP * (columns - 1)) / columns;
            const cellH = (PRINTABLE_MM.h - GAP * (rows - 1)) / rows;
            const aspect = canvas.width / canvas.height;
            const boxW = Math.min(cellW - CELL_PAD * 2, (cellH - CELL_PAD * 2) * aspect);
            // Cells hug the artwork instead of filling the row, so the cut lines
            // sit tight around each sticker.
            const tileW = boxW + CELL_PAD * 2;
            const tileH = boxW / aspect + CELL_PAD * 2;

            const frame = document.createElement('iframe');
            frame.setAttribute('aria-hidden', 'true');
            frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
            document.body.appendChild(frame);

            const cleanup = () => {
                printingRef.current = false;
                setTimeout(() => frame.remove(), 1000);
            };

            frame.onload = () => {
                const doc = frame.contentDocument;
                const win = frame.contentWindow;
                if (!doc || !win) { cleanup(); return; }

                const style = doc.createElement('style');
                style.textContent = `@page{size:A4;margin:${PAGE_MARGIN_MM}mm}
                    body{margin:0;font-family:system-ui,sans-serif}
                    .sheet{display:grid;
                        grid-template-columns:repeat(${columns},${tileW.toFixed(2)}mm);
                        grid-template-rows:repeat(${rows},${tileH.toFixed(2)}mm);
                        gap:${GAP}mm;justify-content:center;align-content:center}
                    .cell{border:1px dashed #ccc;display:flex;align-items:center;justify-content:center}
                    .cell img{width:${boxW.toFixed(2)}mm;height:auto}`;
                doc.head.appendChild(style);

                const sheet = doc.createElement('div');
                sheet.className = 'sheet';
                let pending = perPage;
                // Print only once every copy is decoded, or the page prints blank.
                const oneReady = () => {
                    pending -= 1;
                    if (pending > 0) return;
                    win.focus();
                    win.print();
                    cleanup();
                };
                for (let i = 0; i < perPage; i += 1) {
                    const cell = doc.createElement('div');
                    cell.className = 'cell';
                    const img = doc.createElement('img');
                    img.alt = t('qrCode.previewAlt');
                    img.onload = oneReady;
                    img.onerror = oneReady;
                    img.src = dataUrl;
                    cell.appendChild(img);
                    sheet.appendChild(cell);
                }
                doc.body.appendChild(sheet);
            };

            // about:blank fires `load` on its own, which is when the document exists.
            frame.src = 'about:blank';
        } catch {
            printingRef.current = false;
            notifications.show({ title: t('common.error'), message: t('qrCode.errors.failed'), color: 'red' });
        }
    };

    /**
     * Social targets share the encoded text; "device" hands the PNG to the OS
     * share sheet, falling back to a download where that API is missing.
     */
    const handleShare = async (target) => {
        if (!hasContent) return;
        const text = encodeURIComponent(payload);
        if (target === 'whatsapp') {
            window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
            return;
        }
        if (target === 'facebook') {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${text}`, '_blank', 'noopener,noreferrer');
            return;
        }
        if (target === 'email') {
            window.location.href = `mailto:?subject=${encodeURIComponent(t('qrCode.shareSubject'))}&body=${text}`;
            return;
        }
        try {
            const blob = await exportQrCode(payload, design, { size, format: 'png' });
            const file = new File([blob], outputFileName('png'), { type: 'image/png' });
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: t('qrCode.title') });
            } else {
                downloadBlob(blob, outputFileName('png'));
            }
        } catch {
            // A cancelled share sheet is not an error worth reporting.
        }
    };

    const runBulkExport = async (kind) => {
        if (!bulkRows.length || bulkBusy) return;
        recordToolUsage('image/qr-code', { category: 'image' });
        setBulkBusy(kind);
        setBulkProgress({ done: 0, total: bulkRows.length });
        const onProgress = (done, total) => setBulkProgress({ done, total });
        try {
            const blob = kind === 'zip'
                ? await buildBulkZip(bulkRows, design, size, onProgress)
                : await buildBulkPdf(bulkRows, design, size, onProgress);
            downloadBlob(blob, kind === 'zip' ? 'qr-codes.zip' : 'qr-codes.pdf');
            notifications.show({ title: t('common.success'), message: t('qrCode.done'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('qrCode.errors.failed'), color: 'red' });
        } finally {
            setBulkBusy(null);
            setBulkProgress(null);
        }
    };

    const handleReset = () => {
        renderTokenRef.current += 1;
        customLogoRef.current = null;
        logoAutoRef.current = true;
        setContentType('url');
        setFields(DEFAULT_FIELDS);
        setDesign(DEFAULT_DESIGN);
        setLogoName('');
        setSize(600);
        setPreview(null);
        setRendering(false);
        setCopied(false);
        setError(null);
        setBulkText('');
    };

    const status = deriveToolStatus({
        hasInput: hasContent,
        canRun: hasContent,
        // The first render is the only one that blocks the panel; later renders
        // fade the existing artwork instead so the preview never flashes empty.
        processing: rendering && !preview,
        hasResult: !!preview,
        hasError: !!error,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('qrCode.livePreviewHint'),
        success: t('qrCode.livePreviewReady'),
    });

    const contentFields = useMemo(() => {
        switch (contentType) {
            case 'url':
                return (
                    <Field label={t('qrCode.fields.url')} error={errorFor('url')}>
                        <SuggestInput
                            {...suggestProps('url')}
                            type="url"
                            value={fields.url}
                            onChange={(e) => updateField('url', e.target.value)}
                            placeholder={t('qrCode.placeholders.url')}
                            className={inputClass}
                        />
                    </Field>
                );
            case 'text':
                return (
                    <Field label={t('qrCode.fields.text')} error={errorFor('text')}>
                        <textarea
                            value={fields.text}
                            onChange={(e) => updateField('text', e.target.value)}
                            placeholder={t('qrCode.placeholders.text')}
                            rows={4}
                            className={`${inputClass} resize-y min-h-[96px]`}
                        />
                    </Field>
                );
            case 'wifi':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.ssid')} error={errorFor('ssid')}>
                            <SuggestInput
                                {...suggestProps('ssid')}
                                type="text"
                                value={fields.ssid}
                                onChange={(e) => updateField('ssid', e.target.value)}
                                placeholder={t('qrCode.placeholders.ssid')}
                                className={inputClass}
                            />
                        </Field>
                        <Field label={t('qrCode.fields.encryption')} error={errorFor('encryption')}>
                            <SegRow
                                value={fields.encryption}
                                options={WIFI_ENCRYPTIONS.map((id) => ({ value: id, label: t(`qrCode.wifi.encryption.${id}`) }))}
                                onChange={(v) => updateField('encryption', v)}
                                ariaLabel={t('qrCode.fields.encryption')}
                            />
                        </Field>
                        {fields.encryption !== 'nopass' ? (
                            <Field label={t('qrCode.fields.password')} error={errorFor('password')}>
                                <input
                                    type="text"
                                    value={fields.password}
                                    onChange={(e) => updateField('password', e.target.value)}
                                    placeholder={t('qrCode.placeholders.password')}
                                    className={inputClass}
                                />
                            </Field>
                        ) : null}
                    </div>
                );
            case 'vcard':
            case 'mecard':
                return (
                    <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label={t('qrCode.fields.firstName')} error={errorFor('firstName')}>
                                <input type="text" value={fields.firstName} onChange={(e) => updateField('firstName', e.target.value)} className={inputClass} />
                            </Field>
                            <Field label={t('qrCode.fields.lastName')} error={errorFor('lastName')}>
                                <input type="text" value={fields.lastName} onChange={(e) => updateField('lastName', e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                        <Field label={t('qrCode.fields.phone')} error={errorFor('phone')}>
                            <input type="tel" value={fields.phone} onChange={(e) => updateField('phone', e.target.value)} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.email')} error={errorFor('email')}>
                            <input type="email" value={fields.email} onChange={(e) => updateField('email', e.target.value)} className={inputClass} />
                        </Field>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label={t('qrCode.fields.organization')} error={errorFor('organization')}>
                                <input type="text" value={fields.organization} onChange={(e) => updateField('organization', e.target.value)} className={inputClass} />
                            </Field>
                            {/* MECARD has no TITLE field, so it is vCard-only. */}
                            {contentType === 'vcard' ? (
                                <Field label={t('qrCode.fields.jobTitle')} error={errorFor('jobTitle')}>
                                    <input type="text" value={fields.jobTitle} onChange={(e) => updateField('jobTitle', e.target.value)} className={inputClass} />
                                </Field>
                            ) : null}
                        </div>
                        <Field label={t('qrCode.fields.website')} error={errorFor('website')}>
                            <input type="url" value={fields.website} onChange={(e) => updateField('website', e.target.value)} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.address')} error={errorFor('address')}>
                            <input type="text" value={fields.address} onChange={(e) => updateField('address', e.target.value)} className={inputClass} />
                        </Field>
                    </div>
                );
            case 'email':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.emailTo')} error={errorFor('emailTo')}>
                            <SuggestInput {...suggestProps('emailTo')} type="email" value={fields.emailTo} onChange={(e) => updateField('emailTo', e.target.value)} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.emailSubject')} error={errorFor('emailSubject')}>
                            <input type="text" value={fields.emailSubject} onChange={(e) => updateField('emailSubject', e.target.value)} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.emailBody')} error={errorFor('emailBody')}>
                            <textarea value={fields.emailBody} onChange={(e) => updateField('emailBody', e.target.value)} rows={3} className={`${inputClass} resize-y min-h-[80px]`} />
                        </Field>
                    </div>
                );
            case 'phone':
                return (
                    <Field label={t('qrCode.fields.phoneNumber')} error={errorFor('phoneNumber')}>
                        <SuggestInput {...suggestProps('phoneNumber')} type="tel" value={fields.phoneNumber} onChange={(e) => updateField('phoneNumber', e.target.value)} placeholder={t('qrCode.placeholders.phone')} className={inputClass} />
                    </Field>
                );
            case 'sms':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.smsNumber')} error={errorFor('smsNumber')}>
                            <input type="tel" value={fields.smsNumber} onChange={(e) => updateField('smsNumber', e.target.value)} placeholder={t('qrCode.placeholders.phone')} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.smsMessage')} error={errorFor('smsMessage')}>
                            <textarea value={fields.smsMessage} onChange={(e) => updateField('smsMessage', e.target.value)} rows={3} className={`${inputClass} resize-y min-h-[80px]`} />
                        </Field>
                    </div>
                );
            case 'location':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.locationAddress')} hint={t('qrCode.fields.locationAddressHint')} error={errorFor('locationAddress')}>
                            <input type="text" value={fields.locationAddress} onChange={(e) => updateField('locationAddress', e.target.value)} placeholder={t('qrCode.placeholders.locationAddress')} className={inputClass} />
                        </Field>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label={t('qrCode.fields.latitude')} error={errorFor('latitude')}>
                                <input type="text" inputMode="decimal" value={fields.latitude} onChange={(e) => updateField('latitude', e.target.value)} placeholder={t('qrCode.placeholders.latitude')} className={inputClass} />
                            </Field>
                            <Field label={t('qrCode.fields.longitude')} error={errorFor('longitude')}>
                                <input type="text" inputMode="decimal" value={fields.longitude} onChange={(e) => updateField('longitude', e.target.value)} placeholder={t('qrCode.placeholders.longitude')} className={inputClass} />
                            </Field>
                        </div>
                        <Field label={t('qrCode.fields.locationLabel')} error={errorFor('locationLabel')}>
                            <input type="text" value={fields.locationLabel} onChange={(e) => updateField('locationLabel', e.target.value)} className={inputClass} />
                        </Field>
                    </div>
                );
            case 'event':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.eventTitle')} error={errorFor('eventTitle')}>
                            <input type="text" value={fields.eventTitle} onChange={(e) => updateField('eventTitle', e.target.value)} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.eventLocation')} error={errorFor('eventLocation')}>
                            <input type="text" value={fields.eventLocation} onChange={(e) => updateField('eventLocation', e.target.value)} className={inputClass} />
                        </Field>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label={t('qrCode.fields.eventStart')} error={errorFor('eventStart')}>
                                <input type="datetime-local" value={fields.eventStart} onChange={(e) => updateField('eventStart', e.target.value)} className={inputClass} />
                            </Field>
                            <Field label={t('qrCode.fields.eventEnd')} error={errorFor('eventEnd')}>
                                <input type="datetime-local" value={fields.eventEnd} onChange={(e) => updateField('eventEnd', e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                        <Field label={t('qrCode.fields.eventDescription')} error={errorFor('eventDescription')}>
                            <textarea value={fields.eventDescription} onChange={(e) => updateField('eventDescription', e.target.value)} rows={3} className={`${inputClass} resize-y min-h-[80px]`} />
                        </Field>
                    </div>
                );
            case 'payment': {
                const isUpi = fields.paymentProvider === 'upi';
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.paymentProvider')}>
                            <SegRow
                                value={fields.paymentProvider}
                                options={PAYMENT_PROVIDERS.map((id) => ({ value: id, label: t(`qrCode.paymentProviders.${id}`) }))}
                                onChange={(v) => updateField('paymentProvider', v)}
                                ariaLabel={t('qrCode.fields.paymentProvider')}
                            />
                        </Field>
                        <Field
                            label={isUpi ? t('qrCode.fields.paymentUpiId') : t('qrCode.fields.paymentNumber')}
                            hint={isUpi ? t('qrCode.fields.paymentUpiHint') : t('qrCode.fields.paymentWalletHint')}
                            error={errorFor('paymentNumber')}
                        >
                            <SuggestInput
                                type="text"
                                value={fields.paymentNumber}
                                onChange={(e) => updateField('paymentNumber', e.target.value)}
                                placeholder={isUpi ? 'name@bank' : '01XXXXXXXXX'}
                                className={inputClass}
                                {...suggestProps('paymentNumber')}
                            />
                        </Field>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label={t('qrCode.fields.paymentAmount')} error={errorFor('paymentAmount')}>
                                <input type="text" inputMode="decimal" value={fields.paymentAmount} onChange={(e) => updateField('paymentAmount', e.target.value)} className={inputClass} />
                            </Field>
                            <Field label={t('qrCode.fields.paymentNote')} error={errorFor('paymentNote')}>
                                <input type="text" value={fields.paymentNote} onChange={(e) => updateField('paymentNote', e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                    </div>
                );
            }
            case 'applink':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.appAndroidUrl')} error={errorFor('appAndroidUrl')}>
                            <input type="url" value={fields.appAndroidUrl} onChange={(e) => updateField('appAndroidUrl', e.target.value)} placeholder="https://play.google.com/…" className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.appIosUrl')} hint={t('qrCode.fields.appLinkHint')} error={errorFor('appIosUrl')}>
                            <input type="url" value={fields.appIosUrl} onChange={(e) => updateField('appIosUrl', e.target.value)} placeholder="https://apps.apple.com/…" className={inputClass} />
                        </Field>
                    </div>
                );
            case 'whatsapp':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.whatsappNumber')} hint={t('qrCode.fields.whatsappNumberHint')} error={errorFor('whatsappNumber')}>
                            <SuggestInput {...suggestProps('whatsappNumber')} type="tel" value={fields.whatsappNumber} onChange={(e) => updateField('whatsappNumber', e.target.value)} placeholder={t('qrCode.placeholders.whatsapp')} className={inputClass} />
                        </Field>
                        <Field label={t('qrCode.fields.whatsappMessage')} error={errorFor('whatsappMessage')}>
                            <textarea value={fields.whatsappMessage} onChange={(e) => updateField('whatsappMessage', e.target.value)} rows={3} className={`${inputClass} resize-y min-h-[80px]`} />
                        </Field>
                    </div>
                );
            case 'youtube':
                return (
                    <Field label={t('qrCode.fields.youtubeUrl')} error={errorFor('youtubeUrl')}>
                        <input type="url" value={fields.youtubeUrl} onChange={(e) => updateField('youtubeUrl', e.target.value)} placeholder={t('qrCode.placeholders.youtube')} className={inputClass} />
                    </Field>
                );
            case 'facebook':
            case 'instagram':
            case 'twitter':
            case 'linkedin':
            case 'telegram': {
                const key = `${contentType}User`;
                return (
                    <Field label={t(`qrCode.fields.${key}`)} hint={t('qrCode.fields.handleHint')} error={errorFor(key)}>
                        <input
                            type="text"
                            value={fields[key]}
                            onChange={(e) => updateField(key, e.target.value)}
                            placeholder={t(`qrCode.placeholders.${contentType}`)}
                            className={inputClass}
                        />
                    </Field>
                );
            }
            case 'crypto':
                return (
                    <div className="space-y-3">
                        <Field label={t('qrCode.fields.cryptoCoin')} error={errorFor('cryptoCoin')}>
                            <QrSelect
                                value={fields.cryptoCoin}
                                options={CRYPTO_COINS.map((coin) => ({ value: coin, label: t(`qrCode.coins.${coin}`) }))}
                                onChange={(v) => updateField('cryptoCoin', v)}
                                ariaLabel={t('qrCode.fields.cryptoCoin')}
                            />
                        </Field>
                        <Field label={t('qrCode.fields.cryptoAddress')} error={errorFor('cryptoAddress')}>
                            <input type="text" value={fields.cryptoAddress} onChange={(e) => updateField('cryptoAddress', e.target.value)} className={`${inputClass} font-mono text-[12px]`} />
                        </Field>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Field label={t('qrCode.fields.cryptoAmount')} error={errorFor('cryptoAmount')}>
                                <input type="text" inputMode="decimal" value={fields.cryptoAmount} onChange={(e) => updateField('cryptoAmount', e.target.value)} className={inputClass} />
                            </Field>
                            <Field label={t('qrCode.fields.cryptoLabel')} error={errorFor('cryptoLabel')}>
                                <input type="text" value={fields.cryptoLabel} onChange={(e) => updateField('cryptoLabel', e.target.value)} className={inputClass} />
                            </Field>
                        </div>
                        <Field label={t('qrCode.fields.cryptoMessage')} error={errorFor('cryptoMessage')}>
                            <input type="text" value={fields.cryptoMessage} onChange={(e) => updateField('cryptoMessage', e.target.value)} className={inputClass} />
                        </Field>
                    </div>
                );
            default:
                return null;
        }
    }, [contentType, fields, t, updateField, errorFor, suggestProps]);

    return (
        <>
            {activePreset ? (
                <div ref={logoTileRef} aria-hidden="true" className="pointer-events-none absolute h-0 w-0 overflow-hidden">
                    <LogoPresetTile
                        preset={activePreset}
                        size={256}
                        bg={design.logoBgColor}
                        icon={design.logoIconColor}
                        plate={design.logoPlate}
                        // Square plate: the logo corner-radius slider does the
                        // rounding, so 0% has to mean sharp corners.
                        radius={0}
                    />
                </div>
            ) : null}
            <ToolPageLayout
            slug="image/qr-code"
            subtitleKey="qrCode.subtitle"
            variant="wideControls"
            status={status}
            statusText={statusText}
            showTrust={false}
            onReset={hasContent || preview || logoName ? handleReset : undefined}
            // Everything you do with a finished code sits in the sticky bar:
            // the format to write and the button that writes it, copy beside
            // them, and the print sheet and share targets behind one button,
            // since a per-page selector and four tiles will not fit a bar row.
            // The preview card is left with the artwork and its size slider.
            primaryAction={mode === 'bulk' ? {
                label: bulkBusy === 'zip' ? t('qrCode.bulkWorking') : t('qrCode.bulkZip'),
                onClick: () => runBulkExport('zip'),
                disabled: !bulkRows.length || !!bulkBusy,
                loading: bulkBusy === 'zip',
                icon: IconFileZip,
            } : {
                label: downloadingFormat
                    ? t('qrCode.generating')
                    : t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${format}`) }),
                onClick: () => handleDownload(format),
                disabled: !preview || !!downloadingFormat,
                loading: !!downloadingFormat,
                icon: IconDownload,
            }}
            secondaryAction={mode === 'bulk' ? {
                label: bulkBusy === 'pdf' ? t('qrCode.bulkWorking') : t('qrCode.bulkPdf'),
                onClick: () => runBulkExport('pdf'),
                disabled: !bulkRows.length || !!bulkBusy,
                icon: IconFileTypePdf,
            } : {
                label: copied ? t('qrCode.copied') : t('qrCode.copy'),
                onClick: handleCopy,
                disabled: !preview,
                icon: copied ? IconCheck : IconCopy,
            }}
            actionExtra={mode === 'bulk' ? undefined : (
                <>
                    <OutputFormatSelect
                        formats={QR_OUTPUT_FORMATS}
                        value={format}
                        onChange={setFormat}
                        disabled={!!downloadingFormat}
                    />
                    <QrPrintShare ready={!!preview} onPrint={handlePrint} onShare={handleShare} />
                </>
            )}
            controls={(
                <>
                    <StepCard
                        step={toBnDigits(1)}
                        title={t('qrCode.stepContent')}
                        hint={(
                            // Bulk is a switch on the content step, not a separate
                            // screen: the style, design and frame cards below keep
                            // working on the whole batch.
                            <button
                                type="button"
                                onClick={() => setMode(mode === 'bulk' ? 'single' : 'bulk')}
                                aria-pressed={mode === 'bulk'}
                                className={`flex h-[34px] items-center gap-1.5 rounded-xl border px-3 text-[12.5px] font-semibold transition-colors ${
                                    mode === 'bulk'
                                        ? 'border-primary bg-primary-50 text-primary-700 dark:border-primary dark:bg-primary-950/30 dark:text-primary-400'
                                        : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                                }`}
                            >
                                <IconStack2 size={16} stroke={1.8} />
                                {mode === 'bulk' ? t('qrCode.modeSingle') : t('qrCode.modeBulk')}
                            </button>
                        )}
                    >
                        {mode === 'bulk' ? (
                            <>
                                <p className="mb-2.5 text-[12.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.bulkPasteHint')}</p>
                                <textarea
                                    value={bulkText}
                                    onChange={(e) => setBulkText(e.target.value)}
                                    rows={9}
                                    placeholder={BULK_SAMPLE}
                                    aria-label={t('qrCode.bulkPasteTitle')}
                                    className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
                                />
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <button type="button" onClick={() => setBulkText(BULK_SAMPLE)} className={BULK_ACTION}>
                                        {t('qrCode.bulkSample')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setBulkText('')}
                                        className={`${BULK_ACTION} !text-[#D33A2C] hover:!border-[#D33A2C]/40`}
                                    >
                                        {t('qrCode.bulkClear')}
                                    </button>
                                    <span className="ml-auto text-[12px] text-[#6B635B] dark:text-zinc-400">
                                        {toBnDigits(bulkRows.length)} / {toBnDigits(MAX_BULK_ROWS)}
                                    </span>
                                </div>
                                <p className="mt-3 text-[11.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.bulkStyleHint')}</p>
                            </>
                        ) : (
                            <>
                                {/* The handful of types that cover most codes get chips; the
                                    remaining ones stay in a dropdown so the full list never
                                    costs three rows before the first field. */}
                                <Field label={t('qrCode.contentType')}>
                                    {/* Wide screens: equal-width chips plus a roomier dropdown.
                                        Narrow screens drop the chips entirely — every type lives
                                        in the one full-width dropdown below. */}
                                    <div className="hidden items-center gap-2 lg:flex">
                                        {QUICK_CONTENT_TYPES.map((type) => (
                                            <Hint label={t(`qrCode.types.${type}`)} key={type}>
                                            <button
                                                type="button"
                                                onClick={() => handleContentType(type)}
                                                aria-pressed={contentType === type}
                                                aria-label={t(`qrCode.types.${type}`)}
                                                className={`flex h-[42px] min-w-0 flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 text-[12.5px] font-semibold transition-colors ${
                                                    contentType === type
                                                        ? 'border-primary bg-primary-50 text-primary-700 dark:bg-primary-950/30 dark:text-primary-400'
                                                        : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300'
                                                }`}
                                            >
                                                <span className="flex-none"><QrTypeIcon type={type} size={18} /></span>
                                                <span className="truncate">{t(`qrCode.types.${type}`)}</span>
                                            </button>
                                            </Hint>
                                        ))}
                                        <div className="w-[210px] flex-none [&>div>button]:h-[42px]">
                                            <QrSelect
                                                value={QUICK_CONTENT_TYPES.includes(contentType) ? null : contentType}
                                                options={OTHER_CONTENT_TYPES.map((type) => ({
                                                    value: type,
                                                    label: t(`qrCode.types.${type}`),
                                                    thumb: <QrTypeIcon type={type} size={18} />,
                                                }))}
                                                onChange={handleContentType}
                                                placeholder={t('qrCode.otherTypePlaceholder')}
                                                ariaLabel={t('qrCode.otherType')}
                                            />
                                        </div>
                                    </div>
                                    <div className="[&>div>button]:h-[42px] lg:hidden">
                                        <QrSelect
                                            value={contentType}
                                            options={CONTENT_TYPES.map((type) => ({
                                                value: type,
                                                label: t(`qrCode.types.${type}`),
                                                thumb: <QrTypeIcon type={type} size={18} />,
                                            }))}
                                            onChange={handleContentType}
                                            ariaLabel={t('qrCode.contentType')}
                                        />
                                    </div>
                                </Field>
                                <div className="mt-3.5">{contentFields}</div>
                            </>
                        )}
                    </StepCard>

                    <StepCard step={toBnDigits(2)} title={t('qrCode.stepStyle')} hint={t('qrCode.presetHint')}>
                        <div
                            className="grid gap-2"
                            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))' }}
                        >
                            {STYLE_PRESETS.map((preset) => (
                                <button
                                    key={preset.id}
                                    type="button"
                                    onClick={() => patchDesign(applyStylePreset(preset))}
                                    aria-pressed={isPresetActive(preset, design)}
                                    className={`${swatchBtnClass(isPresetActive(preset, design))} flex-col gap-1.5 py-2.5`}
                                >
                                    <PresetThumb preset={preset} />
                                    <span className="text-[11.5px] font-semibold text-[#3D3833] dark:text-zinc-300">
                                        {t(`qrCode.stylePresets.${preset.id}`)}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <div className="mt-4 border-t border-[#F2EFEC] pt-3.5 dark:border-zinc-800">
                            <div className="mb-2.5 flex items-center gap-2.5">
                                <span className="text-[13.5px] font-bold text-[#1C1815] dark:text-zinc-100">{t('qrCode.templatesTitle')}</span>
                                <button
                                    type="button"
                                    onClick={handleSaveTemplate}
                                    className="ml-auto h-[34px] rounded-xl border border-[#ECE8E4] bg-white px-3 text-[12.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                                >
                                    {t('qrCode.templateSave')}
                                </button>
                            </div>
                            {templates.length === 0 ? (
                                <p className="text-[12.5px] text-[#6B635B] dark:text-zinc-400">{t('qrCode.templatesEmpty')}</p>
                            ) : (
                                <div className="flex flex-wrap gap-2.5">
                                    {templates.map((template) => (
                                        <div key={template.id} className="relative">
                                            <button
                                                type="button"
                                                onClick={() => patchDesign(template.style)}
                                                className="flex w-[86px] flex-col items-center gap-1.5 rounded-[13px] border border-[#ECE8E4] bg-[#FBFAF9] px-2 py-2.5 transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-800/60"
                                            >
                                                <PresetThumb preset={templateThumbStyle(template.style)} />
                                                <span className="max-w-full truncate text-[11px] font-semibold text-[#3D3833] dark:text-zinc-300">
                                                    {template.name}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setTemplates(deleteTemplate(template.id))}
                                                aria-label={t('common.delete')}
                                                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-[#ECE8E4] bg-white text-[12px] leading-none text-[#D33A2C] dark:border-zinc-700 dark:bg-zinc-900"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </StepCard>
                </>
            )}
            settings={(
                <>
                    <QrDesignPanel
                        design={design}
                        patch={patchDesign}
                        logoName={logoName}
                        onLogoPick={handleLogoPick}
                        onLogoRemove={handleLogoRemove}
                        onLogoSelect={handleLogoSelect}
                    />
                    {/* Reference cards live in this column so the rail stays
                        short enough for the preview to keep its pin. History is
                        about single codes, so it sits out of bulk mode. */}
                    {/* Two up only while history is there to fill the other
                        half; on its own the tips card takes the full width. */}
                    <div className={`grid gap-3.5 ${mode === 'single' ? 'lg:grid-cols-2' : ''}`}>
                        {mode === 'single' ? (
                            <QrHistoryCard
                                history={history}
                                onOpen={handleOpenHistory}
                                onDelete={(id) => setHistory(deleteHistoryEntry(id))}
                                onClearAll={() => setHistory(clearHistory())}
                                onSaveCurrent={rememberCode}
                                canSave={hasContent}
                            />
                        ) : null}
                        <QrTipsCard />
                    </div>
                </>
            )}
            preview={(
                // flex-1 gives this column the rail's full height, which is the
                // room the sticky card below needs to stay pinned.
                <div className="flex flex-col gap-3.5 lg:flex-1">
                {/* Only worth showing once there is a code to judge. */}
                {preview ? (
                    <QrScoreCard audit={audit} verifying={verifying} verification={verification} ready />
                ) : null}
                {/* The score card scrolls away; the artwork itself stays pinned. */}
                <div className="lg:sticky lg:top-4">
                <QrPreviewCard
                    preview={preview}
                    transparent={design.transparentBg}
                    stale={rendering}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    error={error}
                    size={size}
                    onSizeChange={setSize}
                    dataLength={payload.length}
                    errorLevel={design.errorLevel}
                    quietZone={design.quietZonePercent}
                    bulk={mode === 'bulk' ? {
                        rows: bulkRows,
                        previews: bulkPreviews,
                        slots: BULK_PREVIEW_COUNT,
                        rendering: bulkRendering,
                        busy: bulkBusy,
                        progress: bulkProgress,
                    } : null}
                />
                </div>
                </div>
            )}
        />
        </>
    );
}
